import React from "react";
import {
	ChoicePill,
	ChoiceRow,
	HiddenControl,
	OptionError,
	OptionField,
	OptionHint,
	OptionLegend,
} from "./optionControls.styles";

/**
 * One wallpaper option that is a CHOICE FROM A CLOSED SET (kitecorp/transferify-ui#1868).
 *
 * One generic control rather than a `WipeDirectionField`: "pick one of these" must offer exactly the
 * values `sponsorOptions.ts` accepts, and a per-option copy of the radio wiring would be one more place
 * for the focus ring and the group naming to drift. It served the retired Light/Dark tone the same way
 * until kitecorp/transferify-ui#2416 replaced that with a colour row. The sender's own page-theme picker
 * (kitecorp/transferify-ui#1869) mounts this same component.
 *
 * Dumb by mandate (epic #26): values in, a name for each, a callback out. It decides nothing about which
 * values exist — that is the shared table's job, and the panel above reads it.
 *
 * A native radio group under the pills, so arrow-key navigation, mutual exclusivity and the
 * "2 of 5" announcement all come from the browser. `name` must therefore be unique on the page, which
 * is what {@link OptionChoiceFieldProps.name} is for.
 */
export interface OptionChoiceFieldProps<T extends string> {
	/** The visible label, which is also the group's accessible name through the `<legend>`. */
	label: string;
	hint: string;
	/** The radio group's form name. Unique per page: two groups sharing one act as a single group. */
	name: string;
	/** Every value on offer, in the order the api declares them. */
	values: readonly T[];
	/** The chosen value, or `undefined` when the advertiser has expressed no preference. */
	value: T | undefined;
	/** The translated word for one value. */
	nameOf: (value: T) => string;
	onChange: (value: T) => void;
	/** An already-translated message, or `null`. */
	error: string | null;
}

export function OptionChoiceField<T extends string>(
	{label, hint, name, values, value, nameOf, onChange, error}: OptionChoiceFieldProps<T>,
): React.ReactElement {
	const errorId = `${name}-error`;
	return (
		<OptionField>
			<OptionLegend>{label}</OptionLegend>
			<OptionHint>{hint}</OptionHint>
			<ChoiceRow>
				{values.map(option => (
					<ChoicePill key={option} $selected={option === value}>
						<HiddenControl
							type="radio"
							name={name}
							value={option}
							checked={option === value}
							// The visible pill text IS the accessible name here, unlike the template cards:
							// a pill carries one word and nothing else, so no explicit label is needed and
							// adding one could only make the two disagree.
							aria-describedby={error === null ? undefined : errorId}
							onChange={() => onChange(option)}
						/>
						{nameOf(option)}
					</ChoicePill>
				))}
			</ChoiceRow>
			{error !== null && <OptionError id={errorId} role="alert">{error}</OptionError>}
		</OptionField>
	);
}
